import { useState } from "react";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { useTaskTimeEntries, useDeleteTimeEntry } from "@/hooks/use-time-registration";
import type { TimeEntry } from "@/hooks/use-time-registration";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SrIcon } from "@/components/ui/SrIcon";
import { RegistrationEditDialog } from "./RegistrationEditDialog";

function hasTimeComponent(dateStr: string): boolean {
  const d = new Date(dateStr);
  return d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
}

function formatTimeRange(startDate: string, durationSeconds: number): string {
  const start = new Date(startDate);
  const end = new Date(start.getTime() + durationSeconds * 1000);
  const hh = (d: Date) => String(d.getHours()).padStart(2, "0");
  const mm = (d: Date) => String(d.getMinutes()).padStart(2, "0");
  return `${hh(start)}:${mm(start)}–${hh(end)}:${mm(end)}`;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}t`;
  return `${h}t ${m}m`;
}

function EntryRow({ entry }: { entry: TimeEntry }) {
  const [editing, setEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteMut = useDeleteTimeEntry();

  return (
    <div className="flex items-center gap-2 py-1 group">
      <span className="text-xs text-muted-foreground w-16">
        {format(new Date(entry.start_date), "d. MMM", { locale: nb })}
      </span>
      {hasTimeComponent(entry.start_date) && (
        <span className="text-xs text-muted-foreground w-20 tabular-nums">
          {formatTimeRange(entry.start_date, entry.duration_seconds)}
        </span>
      )}
      <span className="text-xs font-mono w-12 tabular-nums">{formatDuration(entry.duration_seconds)}</span>
      <span className="text-xs text-muted-foreground flex-1 truncate">
        {entry.comment || ""}
      </span>
      <div className="flex items-center gap-0.5">
        <button
          onClick={() => setEditing(true)}
          className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          title="Rediger"
        >
          <SrIcon name="pencil" className="w-3.5 h-3.5"  />
        </button>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
          title="Slett"
        >
          <SrIcon name="trash2" className="w-3.5 h-3.5"  />
        </button>
      </div>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slett registrering?</AlertDialogTitle>
            <AlertDialogDescription>
              {formatDuration(entry.duration_seconds)} registrert{" "}
              {format(new Date(entry.start_date), "d. MMMM yyyy", { locale: nb })}.
              Denne handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMut.mutate(entry.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RegistrationEditDialog
        entry={editing ? entry : null}
        open={editing}
        onOpenChange={setEditing}
      />
    </div>
  );
}

export default function TaskEntriesRow({
  userId,
  budgetLineId,
  colSpan,
}: {
  userId: string | null;
  budgetLineId: string | null;
  colSpan: number;
}) {
  const { data: entries, isLoading } = useTaskTimeEntries(userId, budgetLineId);

  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        <div className="bg-muted/30 border-t px-6 py-2 space-y-0.5">
          {isLoading && <p className="text-xs text-muted-foreground py-2">Laster...</p>}
          {!isLoading && (!entries || entries.length === 0) && (
            <p className="text-xs text-muted-foreground py-2">Ingen registreringer ennå</p>
          )}
          {entries?.map((entry) => (
            <EntryRow key={entry.id} entry={entry} />
          ))}
        </div>
      </td>
    </tr>
  );
}
